/**
 * Local Cart Service
 * 
 * Handles cart persistence in localStorage for:
 * - Non-authenticated users (primary storage)
 * - Authenticated users (backup/offline storage)
 * 
 * Simple, robust, and MVP-friendly
 */

const CART_STORAGE_KEY = 'artmarket_cart';
const CART_VERSION = '1.0';

/**
 * Save cart items to localStorage
 * @param {Array} items - Cart items array
 * @returns {boolean} Success status
 */
export const saveCartToLocal = (items = []) => {
  try {
    const cartData = {
      version: CART_VERSION,
      timestamp: Date.now(),
      items: items.filter(item => item && (item.id || item.artwork?.id)) // Validate items
    };
    
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartData));
    
    if (import.meta.env.DEV) {
      console.log('💾 [LocalCart] Saved to localStorage:', cartData.items.length, 'items');
    }
    
    return true;
  } catch (error) {
    console.error('❌ [LocalCart] Error saving to localStorage:', error);
    return false;
  }
};

/**
 * Load cart items from localStorage
 * @returns {Array} Cart items array
 */
export const loadCartFromLocal = () => {
  try {
    const stored = localStorage.getItem(CART_STORAGE_KEY);
    
    if (!stored) {
      if (import.meta.env.DEV) {
        console.log('📭 [LocalCart] No cart found in localStorage');
      }
      return [];
    }
    
    const cartData = JSON.parse(stored);
    
    // Validate cart data structure
    if (!cartData.version || !Array.isArray(cartData.items)) {
      console.warn('⚠️ [LocalCart] Invalid cart data format, clearing');
      clearCartFromLocal();
      return [];
    }
    
    // Check if cart is too old (7 days)
    const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
    if (cartData.timestamp < sevenDaysAgo) {
      if (import.meta.env.DEV) {
        console.log('🗑️ [LocalCart] Cart expired (>7 days), clearing');
      }
      clearCartFromLocal();
      return [];
    }
    
    if (import.meta.env.DEV) {
      console.log('📦 [LocalCart] Loaded from localStorage:', cartData.items.length, 'items');
    }
    
    return cartData.items;
  } catch (error) {
    console.error('❌ [LocalCart] Error loading from localStorage:', error);
    clearCartFromLocal(); // Clear corrupted data
    return [];
  }
};

/**
 * Clear cart from localStorage
 * @returns {boolean} Success status
 */
export const clearCartFromLocal = () => {
  try {
    localStorage.removeItem(CART_STORAGE_KEY);
    
    if (import.meta.env.DEV) {
      console.log('🗑️ [LocalCart] Cleared localStorage cart');
    }
    
    return true;
  } catch (error) {
    console.error('❌ [LocalCart] Error clearing localStorage:', error);
    return false;
  }
};

/**
 * Check if there's a cart in localStorage
 * @returns {boolean} Has cart data
 */
export const hasLocalCart = () => {
  try {
    const stored = localStorage.getItem(CART_STORAGE_KEY);
    if (!stored) return false;
    
    const cartData = JSON.parse(stored);
    return Array.isArray(cartData.items) && cartData.items.length > 0;
  } catch {
    return false;
  }
};

/**
 * Add item to localStorage cart
 * @param {Object} artwork - Artwork object
 * @param {number} quantity - Quantity to add
 * @returns {Array} Updated cart items
 */
export const addToLocalCart = (artwork, quantity = 1) => {
  const currentItems = loadCartFromLocal();
  const artworkId = artwork.id;
  
  const existingItemIndex = currentItems.findIndex(item => 
    (item.artwork?.id === artworkId) || (item.id === artworkId)
  );
  
  let updatedItems;
  
  if (existingItemIndex >= 0) {
    // Update quantity if item exists
    updatedItems = [...currentItems];
    updatedItems[existingItemIndex] = {
      ...updatedItems[existingItemIndex],
      quantity: (updatedItems[existingItemIndex].quantity || 1) + quantity
    };
  } else {
    // Add new item
    const newItem = {
      id: `local_${artworkId}_${Date.now()}`, // Unique local ID
      artwork,
      artworkId,
      quantity,
      addedAt: new Date().toISOString()
    };
    updatedItems = [...currentItems, newItem];
  }
  
  saveCartToLocal(updatedItems);
  return updatedItems;
};

/**
 * Remove item from localStorage cart
 * @param {string} artworkId - Artwork ID to remove
 * @returns {Array} Updated cart items
 */
export const removeFromLocalCart = (artworkId) => {
  const currentItems = loadCartFromLocal();
  const updatedItems = currentItems.filter(item => 
    (item.artwork?.id !== artworkId) && (item.id !== artworkId)
  );
  
  saveCartToLocal(updatedItems);
  return updatedItems;
};

/**
 * Update item quantity in localStorage cart
 * @param {string} artworkId - Artwork ID
 * @param {number} quantity - New quantity
 * @returns {Array} Updated cart items
 */
export const updateLocalCartQuantity = (artworkId, quantity) => {
  const currentItems = loadCartFromLocal();
  const itemIndex = currentItems.findIndex(item => 
    (item.artwork?.id === artworkId) || (item.id === artworkId)
  );
  
  if (itemIndex >= 0) {
    const updatedItems = [...currentItems];
    if (quantity <= 0) {
      // Remove item if quantity is 0 or negative
      updatedItems.splice(itemIndex, 1);
    } else {
      updatedItems[itemIndex] = {
        ...updatedItems[itemIndex],
        quantity
      };
    }
    
    saveCartToLocal(updatedItems);
    return updatedItems;
  }
  
  return currentItems;
};

// Export object for easier importing
export const localCartService = {
  saveCart: saveCartToLocal,
  loadCart: loadCartFromLocal,
  clearCart: clearCartFromLocal,
  hasCart: hasLocalCart,
  addItem: addToLocalCart,
  removeItem: removeFromLocalCart,
  updateQuantity: updateLocalCartQuantity
};

export default localCartService;